"use client";

import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import Link from "next/link";
import { Columns2, X, ArrowRight } from "lucide-react";
import { getCompare } from "@/lib/api";
import { GradeBadge } from "@/components/common/grade-badge";
import { RecommendedPairs } from "@/components/compare/recommended-pairs";
import type { WikiProcedureDetail } from "@/types/api";
import { wikiLink } from "@/lib/url";

const STORAGE_KEY = "skinagent_compare_items";
const EVENT = "compare-updated";
const EMPTY: string[] = [];

// getSnapshot 안정 참조 캐시 — raw 문자열이 같으면 동일 배열 반환 (useSyncExternalStore 무한 루프 방지)
let cachedRaw = "__INIT__";
let cachedItems: string[] = EMPTY;

function getStoredItems(): string[] {
  if (typeof window === "undefined") return EMPTY;
  const raw = localStorage.getItem(STORAGE_KEY) || "";
  if (raw !== cachedRaw) {
    cachedRaw = raw;
    try {
      cachedItems = JSON.parse(raw || "[]");
    } catch {
      cachedItems = EMPTY;
    }
  }
  return cachedItems;
}

function subscribe(onChange: () => void) {
  window.addEventListener(EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}

export function ComparePage() {
  const items = useSyncExternalStore<string[]>(subscribe, getStoredItems, () => EMPTY);
  const [procs, setProcs] = useState<WikiProcedureDetail[]>([]);
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    if (items.length < 2) return;
    let cancelled = false;
    getCompare(items)
      .then((data) => {
        if (!cancelled) {
          setProcs(data.procedures || []);
          setFetchError(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setProcs([]);
          setFetchError(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [items]);

  // 로딩은 derived: items는 2개 이상이지만 procs가 아직 없고 에러도 아닐 때
  const loading = items.length >= 2 && procs.length === 0 && !fetchError;

  const writeItems = useCallback((next: string[]) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    window.dispatchEvent(new Event(EVENT));
  }, []);

  const removeItem = (title: string) => {
    writeItems(items.filter((t) => t !== title));
  };

  const clearAll = () => {
    writeItems([]);
  };

  // 빈 상태
  if (items.length < 2 && !loading) {
    return (
      <div className="px-5 py-12 text-center">
        <Columns2 className="text-muted-foreground/30 mx-auto mb-4 h-12 w-12" strokeWidth={1.5} />
        <h2 className="mb-2 text-xl font-bold">시술 비교</h2>
        <p className="text-muted-foreground mb-6 text-sm">
          시술 페이지에서 &ldquo;비교 담기&rdquo; 버튼으로 2~5개를 추가하세요
        </p>

        {/* 사용법 스텝 */}
        <div className="mb-8 flex justify-center gap-4">
          {["탐색에서 시술 찾기", "비교 담기 클릭", "여기서 비교"].map((text, i) => (
            <div key={i} className="flex flex-col items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-violet-700 font-bold text-white shadow-lg shadow-violet-500/20">
                {i + 1}
              </div>
              <span className="text-muted-foreground max-w-[80px] text-center text-xs font-medium">
                {text}
              </span>
            </div>
          ))}
        </div>

        {items.length > 0 && (
          <div className="text-muted-foreground text-sm">
            <p className="mb-2 font-semibold">현재 담긴 항목 ({items.length}개)</p>
            <div className="flex flex-wrap justify-center gap-2">
              {items.map((t) => (
                <span
                  key={t}
                  className="text-primary inline-flex items-center gap-1 rounded-full bg-violet-50 px-3 py-1.5 text-xs font-medium"
                >
                  {t}
                  <button onClick={() => removeItem(t)}>
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        <Link
          href="/wiki/explore"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-violet-500 to-violet-700 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-violet-500/20"
        >
          시술 탐색하기 <ArrowRight className="h-4 w-4" />
        </Link>

        <RecommendedPairs />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-muted-foreground py-16 text-center text-sm">비교 데이터 로딩 중...</div>
    );
  }

  // 비교 테이블
  const rows: { label: string; render: (p: WikiProcedureDetail) => React.ReactNode }[] = [
    {
      label: "유형",
      render: (p) => {
        const labels: Record<string, string> = {
          equipment: "의료기기",
          injectable: "주입제",
          technique: "시술",
        };
        return labels[p.nature] || "-";
      },
    },
    {
      label: "근거 등급",
      render: (p) =>
        p.evidence ? (
          <GradeBadge grade={p.evidence.evidence_grade} />
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    {
      label: "논문 수",
      render: (p) => (p.evidence ? `${p.evidence.total_articles.toLocaleString()}편` : "-"),
    },
    {
      label: "목적",
      render: (p) =>
        p.purposes?.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {p.purposes.map((t) => (
              <span key={t} className="text-primary rounded-full bg-violet-50 px-2 py-0.5 text-xs">
                {t}
              </span>
            ))}
          </div>
        ) : (
          "-"
        ),
    },
    {
      label: "방법",
      render: (p) =>
        p.methods?.filter(Boolean).length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {p.methods.filter(Boolean).map((t) => (
              <span key={t} className="rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700">
                {t}
              </span>
            ))}
          </div>
        ) : (
          "-"
        ),
    },
    {
      label: "부위",
      render: (p) => (p.areas?.length > 0 ? p.areas.join(", ") : "-"),
    },
    {
      label: "안전성",
      render: (p) => {
        if (!p.evidence?.stats?.top_side_effects?.length) return "-";
        const hasSevere = p.evidence.stats.top_side_effects.some((se) => se.severe > 0);
        return (
          <span
            className={`rounded-full px-2.5 py-1 text-xs font-semibold ${hasSevere ? "bg-red-50 text-red-700" : "bg-green-50 text-green-700"}`}
          >
            {hasSevere ? "주의" : "안심"}
          </span>
        );
      },
    },
  ];

  return (
    <div className="px-5 py-6">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-bold">시술 비교</h1>
        <button
          onClick={clearAll}
          className="text-muted-foreground text-xs transition-colors hover:text-red-500"
        >
          전체 삭제
        </button>
      </div>

      <div className="-mx-5 overflow-x-auto px-5">
        <table className="w-full min-w-[480px] border-collapse text-sm">
          <thead>
            <tr>
              <th className="text-muted-foreground sticky left-0 z-10 min-w-[80px] bg-gray-50 p-3 text-left text-xs font-semibold" />
              {procs.map((p, i) => (
                <th
                  key={`h-${p.title}-${i}`}
                  className="min-w-[140px] bg-gray-50 p-3 text-left text-sm font-bold"
                >
                  <Link href={wikiLink(p.title)} className="text-primary hover:underline">
                    {p.title}
                  </Link>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label} className="border-b border-gray-100">
                <td className="text-muted-foreground sticky left-0 z-10 bg-white p-3 text-xs font-semibold whitespace-nowrap shadow-[2px_0_4px_rgba(0,0,0,0.04)]">
                  {row.label}
                </td>
                {procs.map((p, i) => (
                  <td key={`${row.label}-${p.title}-${i}`} className="p-3 align-top">
                    {row.render(p)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
